import AddIcon from '@mui/icons-material/Add';

import { Button } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import numeral from 'numeral';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getAllOrganisations, getAllValidations } from '../api/super-admin';
import { getAllValidations as apiGetAllValidations } from '../api/validations';
import { Validation, ValidationCombined } from '../core/global';
import useLoggedIn from '../hooks/useLoggedIn';
import styles from "../styles/components/super-admin-setting-validations.module.scss";
import { ErrorMessage, WarningMessage } from './error-message';
import { ValidationModal } from './organisation-validations-modal';

export const SettingsValidationsTable = ({
  isPublicValidation = false,
  organisationId = null
}: {
  isPublicValidation?: boolean
  organisationId?: number | string | null
}) => {
  const [openValidationId, setOpenValidationId] = useState<null | number>(null)
  const [allValidations, setAllValidations] = useState<Validation[]>([])
  const [data, setData] = useState<ValidationCombined[]>([])
  const [apiError, setApiError] = useState<string[]>([])
  const [addNewValidation, setAddNewValidation] = useState(false)
  const [allOrganisations, setAllOrganisations] = useState<any[]>([])

  const isLoggedIn = useLoggedIn()
  
  useEffect(() => {
    setAddNewValidation(false)
  }, [])

  useEffect(() => {
    if (!isLoggedIn) {
      setAllOrganisations([])
    } else {
      const isOrganisationLevel =  !!(!isPublicValidation && organisationId) 
      const execute = async () => {
        const res = await getAllOrganisations()
        const {error, response, message} = res
        
        if (error === 200 && response) {
          const results = response?.results || []
          setAllOrganisations(results)
        }
      }
      if (!isOrganisationLevel) {
        execute()
      }
    }
  }, [isPublicValidation, isLoggedIn, organisationId])

  const fetchAllValidations = useCallback(async () => {

    if (!isLoggedIn) {
      setAllValidations([])
    } else {
      if (!isPublicValidation && organisationId) {

        const res = await apiGetAllValidations({
          organisationId,
          privateOnly: true,
        })
    
        const {error, response, message} = res
        
        if (error === 200 && response) {
          // const results = response?.results || []
          if (Array.isArray(response) && response.length > 0) {
            setAllValidations(response as Validation[])
          } else {
            setAllValidations([])
          }
          setApiError([])
        } else {
          setApiError([message || "An error occurred while fetching validations."])
        }
      } else {
        const res = await getAllValidations(
          isPublicValidation
        )
        const {error, response, message} = res
        
        if (error === 200 && response) {
          const results = response?.results || []
          setAllValidations(results as Validation[])
          setApiError([])
        } else {
          setApiError([message || "An error occurred while fetching validations."])
        }
      }
    }
  }, [isPublicValidation, organisationId, isLoggedIn])

  useEffect(() => {
    let res: ValidationCombined[] = []
    if (allValidations) {
      
      const map = new Map<string, any>();

      for (const row of allValidations) {
        // if (!row.organisation_id || row.organisation_id !== id) continue;

        // include organisation to avoid cross-org merging
        const key = `${row.validations_id || row.id}`;
        const [lon, lat] = row.geom.replace(/(POINT)|[()]/gi, '').split(' ')
        
        if (!map.has(key)) {
          map.set(key, {
            organisation_id: row.organisation_id,
            id: row.id,
            model: row.model,
            provider: row.provider,
            geography: row.geography,
            version: row.version,
            name: row.name,
            validations_id: row.validations_id || row.id,
            geom: row.geom,
            lat,
            lon,
            files: [],
          });
        }

        if (row.validations_content_id && row.file) {
          map.get(key).files.push({
            validations_content_id: row.validations_content_id,
            file: row.file,
            content: row.content,
            type: row.type,
          });
        }
      }

      res = Array.from(map.values()) as ValidationCombined[];
    }
    setData(res)
  }, [allValidations])

  useEffect(() => {
    fetchAllValidations()
  }, [])

  const tableColumns = useMemo(() => {
    const base: any[] = [
      {
        field: 'validations_id',
        headerName: 'ID',
        type: 'number',
        width: 80,
        editable: false,
      },
      {
        field: 'name',
        headerName: 'Name',
        width: 300,
        editable: false,
      },
      {
        field: 'lat',
        headerName: 'Lat',
        width: 150,
        editable: false,
        valueFormatter: (params: any) => {
          return params ? numeral(params).format('0.0000') : 'N/A';
        },
      },
      {
        field: 'lon',
        headerName: 'Lon',
        width: 150,
        editable: false,
        valueFormatter: (params: any) => {
          return params ? numeral(params).format('0.0000') : 'N/A';
        },
      },
      {
        field: 'files',
        headerName: 'Files',
        width: 150,
        editable: false,
        valueGetter: (params: any) => {
          if (
            params &&
            Array.isArray(params)
          ) {
            return params.length;
          }
  
          return null;
        },
        valueFormatter: (params: any) => {
          return params ? numeral(params).format('0,0') : 'N/A';
        },
      }
    ];

    const actions: any[] = [{
      field: 'actions',
        headerName: 'Actions',
        width: 150,
        editable: false,
        renderCell: (params: any) => {
          return <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => {
              setOpenValidationId(params?.row.validations_id || null)
            }}
          >Edit</Button>
        }
      }
    ]

    if (!isPublicValidation) {
      return [...base, ...actions]
    }

    const publicColumns: any[] = [
      {
        field: 'organisation_id',
        headerName: 'Shared with',
        width: 350,
        sortable: false,
        editable: false,
        valueGetter: (params: any) => {
          if (
            params &&
            Array.isArray(params)
          ) {
            return params || [];
          }
  
          return null;
        },
        renderCell: (params: any) => {
          const value = params?.value || []
          const org = allOrganisations.filter((o: any) => value.includes(o.id)) 

          if (org && org.length > 0 && org.length === allOrganisations.length) {
            return (<div>All</div>)
          }
          return (
            <div
              style={{
                whiteSpace: 'normal',
                paddingTop: 8,
                paddingBottom: 8,
              }}
            >
              {
                org && org.length > 0 ? org.map((o: any) => o.name).join(', ') : 'N/A'
              }
            </div>)
        }
      }
    ]
    return [...base, ...publicColumns, ...actions]

  }, [isPublicValidation, allOrganisations])
  

  return (
    <div>
      <>
        <Button
          className={styles.addButton}
          variant="contained"
          onClick={() => {
            setAddNewValidation(true)
            // setShowWarning(false)
          }}
          startIcon={<AddIcon />}
        >
          Add validation
        </Button>
      </>
      {
        (
          (!isPublicValidation && organisationId) 
          && (data && data.length === 0)
        ) && <WarningMessage message={'Your organisation has **enabled private validation points**. Please upload your data to take advantage of this feature.'} />
      }
      <ErrorMessage message={apiError} />
      <DataGrid
        rows={data}
        columns={tableColumns}
        initialState={{
          sorting: {
            sortModel: [{ field: 'name', sort: 'asc' }],
          },
          pagination: {
            paginationModel: {
              pageSize: 25,
            },
          },
        }}
        pageSizeOptions={[200]}
        disableRowSelectionOnClick
      />
      {openValidationId && 
        <ValidationModal
          isSuperAdmin={!(!isPublicValidation && organisationId)}
          id={openValidationId}
          onClose={() => {
            setOpenValidationId(null)
            fetchAllValidations()
          }} 
          editedOrganisationId={!isPublicValidation ? organisationId : null} 
        />
      }
      {addNewValidation &&
          <ValidationModal
            isSuperAdmin={!(!isPublicValidation && organisationId)}
            id={null}
            editedOrganisationId={!isPublicValidation ? organisationId : null}
            onClose={() => {
              fetchAllValidations()
              setAddNewValidation(false)
            }}
          />
        }
    </div>
  )
}
